import state from "../state/state";

export const MULTI_TAB_AUTHORING_GUARD_VERSION = 4;

const STATE_PATH = ["appState", "multiTabAuthoring"];
const CHANNEL_NAME = "ambifi-cloud-authoring-tabs-v4";
const STORAGE_EVENT_KEY = "ambifi:authoring-tab:event:v4";
const LEGACY_OWNER_STORAGE_KEYS = [
	"ambifi:authoring-primary-tab:v1",
	"ambifi:authoring-primary-tab:v2",
	"ambifi:authoring-primary-tab:v3"
];
const WINDOW_TAB_ID_KEY = "__ambifiAuthoringTabId";
const WINDOW_TAB_JOINED_AT_KEY = "__ambifiAuthoringTabJoinedAt";
const WINDOW_HEARTBEAT_HANDLE_KEY = "__ambifiAuthoringGuardHeartbeatHandle";
const HEARTBEAT_MS = 3000;
const PEER_STALE_MS = 10000;
const AUTHORING_ROUTE_PREFIXES = [
	"/checklists",
	"/editor",
	"/import",
	"/publications",
	"/activityoperations"
];

let initialized = false;
let heartbeatHandle = null;
let channel = null;
let peers = {};

function normalizeText(value) {
	return String(value || "").trim();
}

function createTabId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function getStableWindowValue(key, createValue) {
	if (typeof window === "undefined") {
		return createValue();
	}

	if (!window[key]) {
		window[key] = createValue();
	}

	return window[key];
}

const TAB_ID = getStableWindowValue(WINDOW_TAB_ID_KEY, createTabId);
const TAB_JOINED_AT = getStableWindowValue(WINDOW_TAB_JOINED_AT_KEY, () => Date.now());

function hasBrowserStorage() {
	try {
		return typeof window !== "undefined" && !!window.localStorage;
	} catch (_err) {
		return false;
	}
}

function getSelectedOrgName(selectedOrg) {
	return normalizeText(
		selectedOrg && (
			selectedOrg.name ||
			selectedOrg.displayName ||
			selectedOrg.organizationName ||
			selectedOrg.label
		)
	);
}

function getCurrentPathname() {
	try {
		return normalizeText(window.location && window.location.pathname).toLowerCase();
	} catch (_err) {
		return "";
	}
}

function isAuthoringRoute(pathname = getCurrentPathname()) {
	const normalizedPathname = normalizeText(pathname).toLowerCase();

	return AUTHORING_ROUTE_PREFIXES.some(prefix => (
		normalizedPathname === prefix ||
		normalizedPathname.indexOf(`${prefix}/`) === 0
	));
}

function getCurrentContext() {
	const selectedOrg = state.get(["selectedOrg"]) || {};
	const loggedIn = state.get(["user", "loggedIn"]) === true;
	const identityId = normalizeText(state.get(["user", "identityId"]));
	const orgId = normalizeText(selectedOrg.id);
	const username = normalizeText(state.get(["user", "username"]));
	const pathname = getCurrentPathname();
	const authoringRoute = isAuthoringRoute(pathname);

	return {
		loggedIn,
		ready: loggedIn && identityId !== "" && orgId !== "" && authoringRoute,
		authoringRoute,
		identityId,
		orgId,
		orgName: getSelectedOrgName(selectedOrg),
		username,
		pathname
	};
}

function clearLegacyOwnerRecords() {
	if (!hasBrowserStorage()) {
		return;
	}

	LEGACY_OWNER_STORAGE_KEYS.forEach((key) => {
		try {
			window.localStorage.removeItem(key);
		} catch (_err) {
			// Ignore cleanup failures.
		}
	});
}

function getPeerLabel(peer) {
	if (!peer) {
		return "";
	}

	const orgName = normalizeText(peer.orgName);
	const orgId = normalizeText(peer.orgId);
	const username = normalizeText(peer.username);
	const orgLabel = orgName || (orgId ? `org ${orgId.slice(0, 3)}` : "");

	if (username && orgLabel) {
		return `${username} in ${orgLabel}`;
	}

	return username || orgLabel;
}

function getWarningMessage(peer, context) {
	const peerLabel = getPeerLabel(peer);
	const ownerContext = peerLabel ? ` (${peerLabel})` : "";
	const peerOrgId = normalizeText(peer && peer.orgId);
	const currentOrgId = normalizeText(context && context.orgId);

	if (peerOrgId && currentOrgId && peerOrgId !== currentOrgId) {
		return `Another AmbiFi authoring tab is open in a different organization${ownerContext}. Review content here only; saving from this tab is blocked to prevent cross-organization content leakage.`;
	}

	return `Another AmbiFi authoring tab is open for this organization${ownerContext}. Review content here only; saving from this tab is blocked to prevent overwriting work from the primary tab.`;
}

function getInactiveState(context, available = true) {
	return {
		version: MULTI_TAB_AUTHORING_GUARD_VERSION,
		available,
		initialized,
		authoringRoute: context.authoringRoute,
		primary: true,
		readOnly: false,
		tabId: TAB_ID,
		primaryTabId: TAB_ID,
		livePeerCount: 0,
		currentIdentityId: context.identityId,
		currentOrgId: context.orgId,
		currentUsername: context.username,
		currentPathname: context.pathname,
		primaryIdentityId: context.identityId,
		primaryOrgId: context.orgId,
		primaryOrgName: context.orgName,
		primaryUsername: context.username,
		primaryPathname: context.pathname,
		message: ""
	};
}

function hasStateChanged(nextState) {
	const currentState = state.get(STATE_PATH) || {};
	const keys = Object.keys(nextState);

	for (let i = 0; i < keys.length; i += 1) {
		const key = keys[i];
		if (currentState[key] !== nextState[key]) {
			return true;
		}
	}

	return false;
}

function setGuardState(nextState) {
	if (hasStateChanged(nextState)) {
		state.set(STATE_PATH, nextState);
	}
}

function prunePeers(now = Date.now()) {
	Object.keys(peers).forEach((tabId) => {
		const peer = peers[tabId];
		if (!peer || (now - Number(peer.lastSeen || 0)) > PEER_STALE_MS) {
			delete peers[tabId];
		}
	});
}

function getSelfPeer(context) {
	return {
		version: MULTI_TAB_AUTHORING_GUARD_VERSION,
		tabId: TAB_ID,
		joinedAt: TAB_JOINED_AT,
		lastSeen: Date.now(),
		identityId: context.identityId,
		orgId: context.orgId,
		orgName: context.orgName,
		username: context.username,
		pathname: context.pathname
	};
}

function getPrimaryPeer(context) {
	const candidates = [getSelfPeer(context)].concat(Object.keys(peers).map(tabId => peers[tabId]));
	candidates.sort((left, right) => {
		const leftJoinedAt = Number(left.joinedAt || 0);
		const rightJoinedAt = Number(right.joinedAt || 0);

		if (leftJoinedAt !== rightJoinedAt) {
			return leftJoinedAt - rightJoinedAt;
		}

		return normalizeText(left.tabId).localeCompare(normalizeText(right.tabId));
	});

	return candidates[0];
}

function getChannel() {
	if (channel || typeof window === "undefined" || typeof window.BroadcastChannel === "undefined") {
		return channel;
	}

	try {
		channel = new window.BroadcastChannel(CHANNEL_NAME);
		channel.onmessage = event => handlePeerMessage(event && event.data);
	} catch (_err) {
		channel = null;
	}

	return channel;
}

function postPeerMessage(message) {
	const resolvedChannel = getChannel();

	if (resolvedChannel) {
		try {
			resolvedChannel.postMessage(message);
		} catch (_err) {
			// Fall through to storage event fallback below.
		}
	}

	if (hasBrowserStorage()) {
		try {
			window.localStorage.setItem(STORAGE_EVENT_KEY, JSON.stringify({
				...message,
				eventId: `${TAB_ID}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
			}));
			window.localStorage.removeItem(STORAGE_EVENT_KEY);
		} catch (_err) {
			// Ignore fallback delivery failures.
		}
	}
}

function announcePresence(context = getCurrentContext()) {
	if (!context.ready) {
		return;
	}

	postPeerMessage({
		type: "presence",
		version: MULTI_TAB_AUTHORING_GUARD_VERSION,
		tabId: TAB_ID,
		joinedAt: TAB_JOINED_AT,
		timestamp: Date.now(),
		identityId: context.identityId,
		orgId: context.orgId,
		orgName: context.orgName,
		username: context.username,
		pathname: context.pathname
	});
}

function announceLeave() {
	postPeerMessage({
		type: "leave",
		version: MULTI_TAB_AUTHORING_GUARD_VERSION,
		tabId: TAB_ID,
		timestamp: Date.now()
	});
}

function handlePeerMessage(message) {
	if (!message || message.version !== MULTI_TAB_AUTHORING_GUARD_VERSION || normalizeText(message.tabId) === "" || message.tabId === TAB_ID) {
		return;
	}

	if (message.type === "leave") {
		delete peers[message.tabId];
		syncMultiTabAuthoringGuard(false);
		return;
	}

	if (message.type !== "presence") {
		return;
	}

	peers[message.tabId] = {
		tabId: normalizeText(message.tabId),
		joinedAt: Number(message.joinedAt || message.timestamp || Date.now()),
		lastSeen: Date.now(),
		identityId: normalizeText(message.identityId),
		orgId: normalizeText(message.orgId),
		orgName: normalizeText(message.orgName),
		username: normalizeText(message.username),
		pathname: normalizeText(message.pathname)
	};

	syncMultiTabAuthoringGuard(false);
}

export function syncMultiTabAuthoringGuard(announce = true) {
	const context = getCurrentContext();
	clearLegacyOwnerRecords();
	prunePeers();

	if (!context.ready) {
		peers = {};
		setGuardState(getInactiveState(context));
		return state.get(STATE_PATH);
	}

	if (announce) {
		announcePresence(context);
	}

	const primaryPeer = getPrimaryPeer(context);
	const readOnly = primaryPeer && primaryPeer.tabId !== TAB_ID;

	setGuardState({
		version: MULTI_TAB_AUTHORING_GUARD_VERSION,
		available: true,
		initialized,
		authoringRoute: context.authoringRoute,
		primary: !readOnly,
		readOnly,
		tabId: TAB_ID,
		primaryTabId: primaryPeer ? normalizeText(primaryPeer.tabId) : TAB_ID,
		livePeerCount: Object.keys(peers).length,
		currentIdentityId: context.identityId,
		currentOrgId: context.orgId,
		currentUsername: context.username,
		currentPathname: context.pathname,
		primaryIdentityId: primaryPeer ? normalizeText(primaryPeer.identityId) : "",
		primaryOrgId: primaryPeer ? normalizeText(primaryPeer.orgId) : "",
		primaryOrgName: primaryPeer ? normalizeText(primaryPeer.orgName) : "",
		primaryUsername: primaryPeer ? normalizeText(primaryPeer.username) : "",
		primaryPathname: primaryPeer ? normalizeText(primaryPeer.pathname) : "",
		message: readOnly ? getWarningMessage(primaryPeer, context) : ""
	});

	return state.get(STATE_PATH);
}

export function initializeMultiTabAuthoringGuard() {
	if (initialized) {
		return syncMultiTabAuthoringGuard();
	}

	initialized = true;

	if (typeof window !== "undefined") {
		getChannel();

		window.addEventListener("storage", (event) => {
			if (!event || event.key !== STORAGE_EVENT_KEY || !event.newValue) {
				return;
			}

			try {
				handlePeerMessage(JSON.parse(event.newValue));
			} catch (_err) {
				// Ignore malformed fallback events.
			}
		});
		window.addEventListener("focus", () => syncMultiTabAuthoringGuard());
		window.addEventListener("visibilitychange", () => syncMultiTabAuthoringGuard());
		window.addEventListener("pagehide", announceLeave);
		window.addEventListener("beforeunload", announceLeave);

		if (window[WINDOW_HEARTBEAT_HANDLE_KEY]) {
			window.clearInterval(window[WINDOW_HEARTBEAT_HANDLE_KEY]);
		}
		heartbeatHandle = window.setInterval(() => syncMultiTabAuthoringGuard(), HEARTBEAT_MS);
		window[WINDOW_HEARTBEAT_HANDLE_KEY] = heartbeatHandle;
	}

	return syncMultiTabAuthoringGuard();
}

export function clearMultiTabAuthoringGuard() {
	announceLeave();
	peers = {};
	setGuardState(getInactiveState(getCurrentContext()));
}

export function getMultiTabAuthoringGuardState() {
	return state.get(STATE_PATH) || getInactiveState(getCurrentContext());
}

export function isCurrentTabAuthoringReadOnly() {
	const guardState = syncMultiTabAuthoringGuard();
	return guardState && guardState.version === MULTI_TAB_AUTHORING_GUARD_VERSION && guardState.readOnly === true;
}

export function getReadOnlyAuthoringMessage() {
	const guardState = getMultiTabAuthoringGuardState();
	return guardState.message || "Another AmbiFi tab is the primary authoring tab. Save authoring changes only in the primary tab.";
}

export function stopMultiTabAuthoringGuardForTests() {
	if (heartbeatHandle && typeof window !== "undefined") {
		window.clearInterval(heartbeatHandle);
		if (window[WINDOW_HEARTBEAT_HANDLE_KEY] === heartbeatHandle) {
			window[WINDOW_HEARTBEAT_HANDLE_KEY] = null;
		}
	}
	if (channel) {
		try {
			channel.close();
		} catch (_err) {
			// Ignore channel cleanup failures.
		}
	}
	heartbeatHandle = null;
	channel = null;
	peers = {};
	initialized = false;
}
